import { useMemo } from 'react';
import { Database, Loader2 } from 'lucide-react';
import {
  useErpFieldCatalog,
  useErpFieldConfig,
} from '@api/hooks/useErpFields';

const SURFACE_LABELS: Record<string, string> = {
  document_review: 'Document Review',
  document_approval: 'Document Approval',
  ap_posting: 'AP Posting',
};

export const ErpFieldConfiguration = () => {
  const catalogQuery = useErpFieldCatalog();
  const configQuery = useErpFieldConfig();

  const configByFieldId = useMemo(() => {
    return new Map((configQuery.data || []).map((config) => [config.field_definition_id, config]));
  }, [configQuery.data]);

  const fields = catalogQuery.data?.fields || [];
  const counts = catalogQuery.data?.synced_option_counts || {};

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">ERP Fields</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            ERP field catalog discovered from the active integration. Document review fields are added from the review modal; AP posting fields are configured where posting is reviewed.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Database className="h-4 w-4" />
          {Object.entries(counts).map(([key, value]) => `${key}: ${value}`).join(' · ') || 'No synced options yet'}
        </div>
      </div>

      {(catalogQuery.isLoading || configQuery.isLoading) && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading ERP field catalog...
        </div>
      )}

      {fields.length > 0 && (
        <div className="overflow-hidden rounded border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Field</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Surface</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
              {fields.map((field) => {
                const config = configByFieldId.get(field.id);
                const enabled = config?.enabled ?? true;
                const memoryEnabled = config?.memory_enabled ?? false;
                return (
                  <tr key={field.id}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{field.label}</p>
                      <p className="text-xs text-gray-500">{field.field_key}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {SURFACE_LABELS[field.surface] || field.surface}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {field.value_type}{field.object_type ? ` · ${field.object_type}` : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {enabled ? 'Added' : 'Available'}
                      {memoryEnabled ? ' · memory' : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {fields.length === 0 && !catalogQuery.isLoading && (
        <div className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          Connect an ERP integration to discover configurable fields.
        </div>
      )}
    </section>
  );
};
